import React, { Component } from "react";
import { Form, Table, Image, Modal, Button } from "react-bootstrap";
import { PiWarning } from "react-icons/pi";
import axios from "../../axios";
import axiosLib from "axios";
import apiError from "../../error";
import ConfiguredModal from "../launch-modal/configured-modal";
import ReplyModal from "../campaigns/results/reply-modal";
import simulationandtrain from "../../assets/create_campaign/simulationandtrain.png";
import { DEFAULT_COURSE_OPTIONS } from "./training-defaults";
import "../launch-modal/steps/steps.css";

const getCancelSource = () => {
	const CancelToken = axiosLib.CancelToken;
	return CancelToken ? CancelToken.source() : null;
};

/**
 * "Configure Training" modal — two tabs:
 *   • Course Settings          — selected courses, each with Configure / Remove
 *   • Configure Email Template — Send Course / Completion / Quiz-fail dropdowns
 *
 * Selection state lives in the parent; this component owns only the tab, the
 * per-course settings sub-modal and the email-template list it fetches itself.
 * Shared by the web launch wizard and the AI/MCP chat picker.
 */
export default class ConfigureTrainingModal extends Component {
	constructor(props) {
		super(props);
		this.templatesCancel = null;
		this.state = {
			configureTab: props.initialTab || "course",
			templates: {},
			// per-course settings modal
			showCourseSettings: false,
			settingsCourseId: null,
			settingsOptions: {},
			// email preview
			showPreview: false,
			previewHtml: null,
		};
	}

	componentDidMount() {
		this.loadCourseTemplates();
	}

	componentDidUpdate(prevProps) {
		/* Re-open always lands on the tab the parent asked for (the wizard sends
		   the user straight to whichever half is still incomplete). */
		if (!prevProps.show && this.props.show) {
			this.setState({ configureTab: this.props.initialTab || "course" });
		}
	}

	componentWillUnmount() {
		if (this.templatesCancel) this.templatesCancel();
	}

	loadCourseTemplates = () => {
		const source = getCancelSource();
		if (source) this.templatesCancel = source.cancel;
		axios
			.get("/api/lms/template-list-threatcop", { cancelToken: source ? source.token : undefined })
			.then((res) => this.setState({ templates: res.data.templates || {} }))
			.catch((err) => { if (!axiosLib.isCancel(err)) apiError(err); });
	};

	loadTemplatePreview = (templateType) => {
		const { templateIds = {} } = this.props;
		const idMap = {
			course: templateIds.sendCourseTemplateId,
			completion: templateIds.courseCompletionTemplateId,
			quiz_fail: templateIds.courseQuizFailReminderTemplateId,
		};
		axios
			.get("/api/lms/template-preview-threatcop", { params: { id: idMap[templateType] || 0, templateType } })
			.then((res) => this.setState({ showPreview: true, previewHtml: res.data.html }))
			.catch((err) => apiError(err));
	};

	openCourseSettings = (courseId) => {
		const existing = this.props.options[courseId] || { ...DEFAULT_COURSE_OPTIONS };
		this.setState({ showCourseSettings: true, settingsCourseId: courseId, settingsOptions: existing });
	};

	setEmailTab = () => {
		this.setState({ configureTab: "email" });
		if (this.props.onEmailTabVisited) this.props.onEmailTabVisited();
	};

	/** Every selected course must have its per-course settings configured. */
	allCoursesConfigured = () =>
		this.props.selectedCourses.length > 0 &&
		this.props.selectedCourses.every((id) => !!this.props.options[id]);

	/** Footer action: block until course settings + email templates are done.
		Gated on which tab is actually showing (not `emailTemplateTabVisited`) —
		that prop can already be true on open (e.g. the AI picker seeds it from
		"does this training already have courses saved from before", not from
		"was the email tab actually shown in this session"), which used to make
		"Next" jump straight to onSave() and close the modal without ever
		switching to the email tab. */
	handleNext = () => {
		if (!this.allCoursesConfigured()) {
			this.setState({ configureTab: "course" });
			return;
		}
		if (this.state.configureTab !== "email") {
			this.setEmailTab();
			return;
		}
		if (this.props.onSave) this.props.onSave();
	};

	render() {
		const { configureTab, templates } = this.state;
		const {
			show,
			onHide,
			selectedCourses = [],
			options = {},
			courseName = {},
			imageURL = "",
			templateIds = {},
			onTemplateIdChange,
			onRemoveCourse,
			onSetCourseOptions,
		} = this.props;

		return (
			<>
				<Modal show={show} onHide={onHide} centered className="commonModal" size="lg">
					<Modal.Header closeButton>
						<div>
							<h5 className="mb-0">Configure Training</h5>
							<p className="mb-0 text-muted" style={{ fontSize: "13px" }}>Define your training settings.</p>
						</div>
					</Modal.Header>
					<Modal.Body>
						<div className="configure-training-tabs">
							<button
								type="button"
								className={`configure-training-tab ${configureTab === "course" ? "active" : ""}`}
								onClick={() => this.setState({ configureTab: "course" })}
							>
								Course Settings
							</button>
							<button
								type="button"
								className={`configure-training-tab ${configureTab === "email" ? "active" : ""}`}
								onClick={this.setEmailTab}
							>
								Configure Email Template
							</button>
						</div>

						{configureTab === "course" ? (
							<div className="tableContainerWrapInner" style={{ overflowX: "auto" }}>
								<Table responsive className="mb-0 configure-training-table">
									<thead>
										<tr>
											<th>Title</th>
											<th className="text-center">Course Settings</th>
											<th className="text-center">Action</th>
										</tr>
									</thead>
									<tbody>
										{selectedCourses.length === 0 ? (
											<tr><td colSpan="3" className="text-center text-muted py-4">No courses selected</td></tr>
										) : selectedCourses.map((courseId) => {
											const isConfigured = !!options[courseId];
											return (
												<tr key={courseId}>
													<td>
														<div className="d-flex align-items-center">
															<Image src={imageURL + (courseName[courseId]?.logo || "")} alt="" height="34px" width="34px"
																className="mr-2" onError={(e) => { e.target.src = simulationandtrain; }} />
															<span>{courseName[courseId]?.name || `Course #${courseId}`}</span>
														</div>
													</td>
													<td className="text-center">
														<button type="button" className={`configure-setting-btn ${isConfigured ? "configured" : ""}`} onClick={() => this.openCourseSettings(courseId)}>
															<PiWarning /> {isConfigured ? "Configured" : "Configure Setting"}
														</button>
													</td>
													<td className="text-center">
														<button type="button" className="configure-remove-btn" onClick={() => onRemoveCourse && onRemoveCourse(courseId)}>⊖ Remove</button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</Table>
							</div>
						) : (
							<>
								<div className="alert alert-primary" style={{ fontSize: "12px", padding: "7px 10px" }}>
									<b>Note:</b> You can choose from the custom templates you've created in the TLMS portal or use the default templates provided.
								</div>
								<div className="tableContainerWrapInner" style={{ overflowX: "auto" }}>
									<Table responsive className="mb-0 configure-training-table">
										<thead>
											<tr>
												<th style={{ width: "40%" }}>Template</th>
												<th>Select Template</th>
												<th className="text-center">Preview</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>Send Course</td>
												<td>
													<Form.Control as="select" value={templateIds.sendCourseTemplateId || ""} onChange={(e) => onTemplateIdChange && onTemplateIdChange("sendCourseTemplateId", e.target.value)}>
														{(templates.course || []).map((t, k) => t && <option key={"c" + k} value={t.id}>{t.name}</option>)}
													</Form.Control>
												</td>
												<td className="text-center setlinkColor" onClick={() => this.loadTemplatePreview("course")}><a href="#" onClick={(e) => e.preventDefault()}>view</a></td>
											</tr>
											<tr>
												<td>Course Completion</td>
												<td>
													<Form.Control as="select" value={templateIds.courseCompletionTemplateId || ""} onChange={(e) => onTemplateIdChange && onTemplateIdChange("courseCompletionTemplateId", e.target.value)}>
														{(templates.completion || []).map((t, k) => t && <option key={"cc" + k} value={t.id}>{t.name}</option>)}
													</Form.Control>
												</td>
												<td className="text-center setlinkColor" onClick={() => this.loadTemplatePreview("completion")}><a href="#" onClick={(e) => e.preventDefault()}>view</a></td>
											</tr>
											<tr>
												<td>Course Quiz Fail Reminder</td>
												<td>
													<Form.Control as="select" value={templateIds.courseQuizFailReminderTemplateId || ""} onChange={(e) => onTemplateIdChange && onTemplateIdChange("courseQuizFailReminderTemplateId", e.target.value)}>
														{(templates.quiz_fail || []).map((t, k) => t && <option key={"qf" + k} value={t.id}>{t.name}</option>)}
													</Form.Control>
												</td>
												<td className="text-center setlinkColor" onClick={() => this.loadTemplatePreview("quiz_fail")}><a href="#" onClick={(e) => e.preventDefault()}>view</a></td>
											</tr>
										</tbody>
									</Table>
								</div>
							</>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={onHide}>Cancel</Button>
						<Button variant="success" onClick={this.handleNext}>{configureTab === "email" ? "Save" : "Next"}</Button>
					</Modal.Footer>
				</Modal>

				<ConfiguredModal
					showModal={this.state.showCourseSettings}
					showHandle={() => this.setState({ showCourseSettings: false })}
					courseId={this.state.settingsCourseId}
					options={this.state.settingsOptions}
					setOptions={onSetCourseOptions}
				/>
				<ReplyModal
					buffer={this.state.previewHtml}
					show={this.state.showPreview}
					handleClose={() => this.setState({ showPreview: false })}
				/>
			</>
		);
	}
}
